import { LoaderFunction, LoaderFunctionArgs, redirect } from 'react-router-dom';

import { getApplicationError } from './errors';
import { ApplicationErrorCode } from '.';
import {
  ApolloClient,
  ApolloError,
  MaybeMasked,
  NormalizedCacheObject,
  OperationVariables,
  QueryOptions
} from '@apollo/client';

export interface queryOptions {
  notFoundRedirect?: string;
  suppressAuthErrors?: boolean;
}

export const query = async <Q, V extends OperationVariables, R>(
  client: ApolloClient<NormalizedCacheObject>,
  queryOptions: QueryOptions<V, Q>,
  transform: (data: MaybeMasked<Q>) => R | null | undefined | Promise<R | null | undefined>,
  opts?: queryOptions
) => {
  const { data, errors } = await client.query<Q, V>(queryOptions);

  const error = errors?.length ? new ApolloError({ graphQLErrors: errors }) : undefined;
  const applicationError = getApplicationError(error);

  if (applicationError?.code === ApplicationErrorCode.Unauthorized) {
    if (opts?.suppressAuthErrors) {
      return Promise.resolve(transform(data));
    }
    return redirect('/signin');
  } else if (applicationError?.code === ApplicationErrorCode.NotFound && opts?.notFoundRedirect) {
    return redirect(opts.notFoundRedirect);
  } else if (error) {
    throw error;
  }

  return Promise.resolve(transform(data));
};

// Mark the promise the way react-router's <Await> tracks one, as v6's defer() did, so data that
// has already settled renders without suspending into the fallback again.
const track = <T,>(promise: Promise<T>): Promise<T> => {
  Object.defineProperty(promise, '_tracked', { get: () => true });
  promise.then(
    (data) => {
      Object.defineProperty(promise, '_data', { get: () => data });
    },
    (error) => {
      Object.defineProperty(promise, '_error', { get: () => error });
    }
  );
  return promise;
};

export const merge =
  (loaders: Record<string, LoaderFunction>) => async (args: LoaderFunctionArgs) => {
    return {
      data: track(
        (async () => {
          // Initiate all promises. If any reject, the await will cause the entire promise to reject.
          const promises = await Promise.all(Object.values(loaders).map((loader) => loader(args)));

          // Map the promises back to their keys and return the object
          return Object.fromEntries(
            Object.keys(loaders).map((key, index) => [key, promises[index]])
          );
        })()
      )
    };
  };
